import React, { useRef, useState, useEffect, useMemo } from "react";
import { Avatar } from "primereact/avatar";
import { Menu } from "primereact/menu";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import { AuthService } from "../services/AuthService";
import { DriverService } from "../services/DriverService";
import { Driver } from "../interfaces/drivers.interface";

// Import logo
import Logo from "../assets/logo-prodrive.svg";

const TopBar: React.FC = () => {
  const history = useHistory();
  const [, , removeCookie] = useCookies(["Authorization"]);
  const [user, setUser] = useState<Driver | null>(null);
  const menuRef = useRef<Menu>(null);
  const authService = new AuthService();
  const driverService = useMemo(() => new DriverService(), []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await driverService.getCurrentDriver();
        // console.log("Fetched user data:", response);
        if (
          response.data &&
          response.data.data &&
          typeof response.data.data === "object"
        ) {
          setUser(response.data.data);
          // console.log("Set user data:", response.data.data);
        } else {
          console.error("Invalid user data structure:", response);
        }
      } catch (error) {
        console.error("Error fetching current driver:", error);
      }
    };

    fetchUser();

    const intervalId = setInterval(fetchUser, 60000 * 10); // Check every 10 minutes

    return () => clearInterval(intervalId);
  }, [driverService]);

  const handleLogout = async () => {
    try {
      await authService.logOut();
      localStorage.clear();
      removeCookie("Authorization");
      history.push("/");
    } catch (err) {
      console.error(err);
      localStorage.clear();
      removeCookie("Authorization");
      history.push("/");
    }
  };

  const getInitial = () => {
    if (user && user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return "C";
  };

  const getUserName = () => {
    if (user && user.name) {
      return user.name;
    }
    return "Chauffeur";
  };

  const isExpirationApproaching = () => {
    if (user && user.expirationDate) {
      const expirationDate = new Date(user.expirationDate);
      const today = new Date();
      const daysUntilExpiration = Math.ceil(
        (expirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24)
      );
      return daysUntilExpiration <= 3;
    }
    return false;
  };

  const getStatusDetails = () => {
    if (!user) {
      return { color: "bg-gray-600", text: "Inconnu" };
    }

    const isManuallyActive = user.manuallyActive ?? false;
    const isNotExpired = user.expirationDate
      ? new Date() < new Date(user.expirationDate)
      : false;

    if (isManuallyActive && isNotExpired) {
      return { color: "bg-green-600", text: "Actif" };
    } else if (!isManuallyActive && isNotExpired) {
      return { color: "bg-yellow-600", text: "Manuellement inactif" };
    } else if (isManuallyActive && !isNotExpired) {
      return { color: "bg-orange-600", text: "Expiré" };
    } else {
      return { color: "bg-red-600", text: "Inactif" };
    }
  };

  const menuItems = [
    {
      label: "Déconnexion",
      icon: "pi pi-sign-out",
      command: handleLogout,
    },
  ];

  const statusDetails = getStatusDetails();

  const getDaysUntilExpiration = () => {
    if (user && user.expirationDate) {
      const expirationDate = new Date(user.expirationDate);
      const today = new Date();
      const daysUntilExpiration = Math.ceil(
        (expirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24)
      );
      return daysUntilExpiration;
    }
    return 0;
  };

  return (
    <div className="flex justify-between items-center bg-slate-50 py-2 px-4 shadow-md topbar">
      <img src={Logo} alt="Logo ProDrive" className="h-6 sm:h-8" />
      {isExpirationApproaching() && (
        <div className="ml-4 text-xs text-yellow-600">
          Attention: Votre compte expire dans {getDaysUntilExpiration()} jours
        </div>
      )}
      <div className="flex items-center">
        <Menu model={menuItems} popup ref={menuRef} />
        <div
          className="flex items-center cursor-pointer"
          onClick={(e) => menuRef.current?.toggle(e)}
        >
          <Avatar
            label={getInitial()}
            shape="square"
            className={`mr-2 sm:mr-3 topbar-avatar rounded-lg min-h-full w-10 h-10 bg-primary`}
            style={{ color: "#ffffff" }}
          />
          <div className="flex flex-col">
            <span className="font-semibold text-gray-800 text-xs sm:text-base">
              {getUserName()}
            </span>
            <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">
              {statusDetails.text}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
